import type { ColorGradient } from "types/gaugePlot.types"

/**
 * Extra degrees the colored arc extends past the pointer dot so the dot sits
 * inside the bar instead of at its tip.
 *
 * Also used by MuiGaugeChart to pad the reference arc by the same amount on
 * both sides, keeping it symmetric regardless of the current value.
 */
export const ANGULAR_OVERSHOOT_DEG = 1

// Number of stroke segments used to approximate the arc gradient.
// Higher = smoother color transitions along the arc.
export const N_SEGMENTS = 60

// Number of tick marks rendered along the gauge arc.
export const TICK_COUNT = 26

export function parseColor(color: string): [number, number, number] {
  const hex = /^#([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(color)
  if (hex) return [parseInt(hex[1], 16), parseInt(hex[2], 16), parseInt(hex[3], 16)]
  const rgb = /^rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/.exec(color)
  if (rgb) return [parseInt(rgb[1]), parseInt(rgb[2]), parseInt(rgb[3])]
  return [0, 0, 0]
}

export function interpolateColor(stops: ColorGradient[], t: number): string {
  const clamped = Math.max(0, Math.min(1, t))
  const upper = stops.find((s) => s.offset >= clamped) ?? stops[stops.length - 1]
  const lower = [...stops].reverse().find((s) => s.offset <= clamped) ?? stops[0]
  if (lower === upper) return lower.color
  const ratio = (clamped - lower.offset) / (upper.offset - lower.offset)
  const [r1, g1, b1] = parseColor(lower.color)
  const [r2, g2, b2] = parseColor(upper.color)
  return `rgb(${Math.round(r1 + ratio * (r2 - r1))},${Math.round(g1 + ratio * (g2 - g1))},${Math.round(b1 + ratio * (b2 - b1))})`
}

const DEFAULT_LABEL_COUNT = 6

export const getLabelValues = (
  domainMin: number,
  domainMax: number,
  valueInterval?: number,
  labelCount?: number,
): number[] => {
  const range = domainMax - domainMin

  if (valueInterval !== undefined && valueInterval > 0) {
    const values: number[] = []
    const first = Math.ceil(domainMin / valueInterval) * valueInterval
    for (let v = first; v <= domainMax; v += valueInterval) {
      values.push(v)
    }
    if (values.length === 0 || values[0] !== domainMin) values.unshift(domainMin)
    if (values[values.length - 1] !== domainMax) values.push(domainMax)
    return values
  }

  const count = Math.max(labelCount ?? DEFAULT_LABEL_COUNT, 2)
  return Array.from({ length: count }, (_, i) => {
    if (i === count - 1) return domainMax
    return Math.round(domainMin + (i / (count - 1)) * range)
  })
}
